import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ControlOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SettingOutlined,
  StopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type CustomElement, customElementsApi } from '@/api/customElements';
import {
  type ReorderDirection,
  type WorkflowStep,
  type WorkflowStepType,
  type WorkflowTemplate,
  workflowsApi,
} from '@/api/workflows';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { AdditionalDocumentEntriesDrawer } from './AdditionalDocumentEntriesDrawer';
import { AddStepModal } from './AddStepModal';
import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';
import { hasEligibleSourceFields } from './conditions/sourceFields';
import { EditStepModal } from './EditStepModal';
import { stripStepForCustomElement } from './form-builder/customElements/conditionStripping';
import { SaveAsCustomElementModal } from './form-builder/customElements/SaveAsCustomElementModal';
import { sectionToPayload } from './form-builder/customElements/toCustomPayload';
import { OneSpanConfigDrawer } from './OneSpanConfigDrawer';
import { summariseOneSpanConfig } from './oneSpanHelpers';
import { PdfEntriesDrawer } from './PdfEntriesDrawer';
import { StepAdvancedModal } from './StepAdvancedModal';
import { isPlatformManaged, metaFor, stepDefaults, stepTypeLabel } from './stepTypes';
import { ThankYouConfigModal } from './ThankYouConfigModal';
import { WelcomeConfigModal } from './WelcomeConfigModal';

const { Text } = Typography;

type Props = {
  workflow: WorkflowTemplate;
  /** Called after any mutation so the parent can refresh the template (steps live on it). */
  onChanged: (next: WorkflowTemplate) => void;
  /**
   * True when another Originator currently holds the editor lock — every mutation control
   * is disabled and the BE would 409 the call anyway. Defaults to false so existing
   * call sites that don't know about the lock keep working.
   */
  lockedByOther?: boolean;
  /** True when the current user holds the lock; unused for gating today but reserved for future UX (e.g. a "lock held" badge). */
  lockedHeld?: boolean;
};

const CARD_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '14px 16px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff',
};

const NUMBER_CIRCLE: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: '#111827',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 600,
  flexShrink: 0,
};

export function WorkflowSteps({ workflow, onChanged, lockedByOther = false }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isFr = i18n.language.startsWith('fr');
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WorkflowStep | null>(null);
  const [busyStepId, setBusyStepId] = useState<string | null>(null);
  const [pdfSettingsTarget, setPdfSettingsTarget] = useState<WorkflowStep | null>(null);
  const [extensionsTarget, setExtensionsTarget] = useState<WorkflowStep | null>(null);
  const [oneSpanTarget, setOneSpanTarget] = useState<WorkflowStep | null>(null);
  const [welcomeTarget, setWelcomeTarget] = useState<WorkflowStep | null>(null);
  const [thankYouTarget, setThankYouTarget] = useState<WorkflowStep | null>(null);
  const [visibilityTarget, setVisibilityTarget] = useState<WorkflowStep | null>(null);
  // The Advanced panel behind the `…` in the Edit Step header. Stacked ON TOP of the edit
  // modal rather than replacing it (the Save-as-custom button's pattern), so an unsaved
  // title edit survives a trip into the branch rules.
  const [advancedTarget, setAdvancedTarget] = useState<WorkflowStep | null>(null);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [disableTarget, setDisableTarget] = useState<WorkflowStep | null>(null);
  const [savingDisable, setSavingDisable] = useState(false);
  const [saveStepTarget, setSaveStepTarget] = useState<WorkflowStep | null>(null);
  const [savingStepAsCustom, setSavingStepAsCustom] = useState(false);

  // Status gate stays the same; the lock just adds another way for editing to be off-limits.
  const editable =
    (workflow.status === 'Draft' || workflow.status === 'Rejected') && !lockedByOther;
  // Show EVERY step the advisor walks, in the order they walk it, numbered
  // continuously — the editor's job is to reflect the runtime.
  //
  // This list used to (a) drop anything with `originatorAddable: false`, which
  // hid Application Review, and (b) split the rest across a numbered section and
  // an unnumbered one that put E-Signature after steps it actually precedes.
  // Between them the editor's numbers drifted two places from the preview rail:
  // Document Upload read as 11 here and 13 there. `originatorAddable` now means
  // only what its name says — whether the Add-Step picker offers it.
  //
  // The API already returns canonical runtime order (WorkflowTemplate
  // .ReorderCanonically), which is the same order FormShell renders, so there is
  // nothing to sort here — echoing the array IS the fix.
  const steps = workflow.steps ?? [];
  const dynamicForms = steps.filter((s) => s.type === 'DynamicForm');

  const refreshTemplate = async () => {
    try {
      const fresh = await workflowsApi.get(workflow.id);
      onChanged(fresh);
    } catch (err) {
      handleApiError(err, t, t('workflow.details.loadFailed'));
    }
  };

  const handleAdd = async (type: WorkflowStepType) => {
    setAddOpen(false);
    try {
      const d = stepDefaults(type, dynamicForms.length, t);
      // Send both EN and FR so the step row is bilingual from the start; the FE card
      // renders whichever language matches the user's locale.
      await workflowsApi.addStep(workflow.id, {
        type,
        titleEn: d.titleEn,
        titleFr: d.titleFr,
        descriptionEn: d.descriptionEn || undefined,
        descriptionFr: d.descriptionFr || undefined,
      });
      message.success(t('workflow.steps.added'));
      await refreshTemplate();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.addFailed'));
    }
  };

  const handleAddCustom = async (el: CustomElement) => {
    setAddOpen(false);
    try {
      await workflowsApi.placeCustomStep(workflow.id, el.id);
      message.success(t('workflow.steps.added'));
      await refreshTemplate();
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.insertStepFailed'));
    }
  };

  const handleEdit = (step: WorkflowStep) => setEditTarget(step);

  const handleEditSubmit = async (values: {
    titleEn: string;
    titleFr?: string;
    /** Non-optional here on purpose: the modal always sends both, so an emptied field reaches
     *  the API as `''` (clear) instead of disappearing from the payload (leave as is). */
    navTitleEn: string;
    navTitleFr: string;
    descriptionEn?: string;
    descriptionFr?: string;
    advisorOnly?: boolean;
  }) => {
    if (!editTarget) return;
    try {
      await workflowsApi.editStep(workflow.id, editTarget.id, values);
      message.success(t('workflow.steps.edited'));
      setEditTarget(null);
      await refreshTemplate();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.editFailed'));
    }
  };

  const handleSaveStepAsCustom = async ({
    nameEn,
    nameFr,
  }: {
    nameEn: string;
    nameFr: string | null;
  }) => {
    if (!saveStepTarget) return;
    setSavingStepAsCustom(true);
    try {
      const strippedSections = stripStepForCustomElement(
        saveStepTarget.sections,
        saveStepTarget.id,
      );
      await customElementsApi.saveStep(nameEn, nameFr, saveStepTarget.id, {
        stepTitleEn: saveStepTarget.titleEn,
        stepTitleFr: saveStepTarget.titleFr,
        // Carried so saving a step to the library and placing it back doesn't silently
        // revert its rail label to the long page title.
        stepNavTitleEn: saveStepTarget.navTitleEn ?? undefined,
        stepNavTitleFr: saveStepTarget.navTitleFr ?? undefined,
        stepDescriptionEn: saveStepTarget.descriptionEn,
        stepDescriptionFr: saveStepTarget.descriptionFr,
        sections: strippedSections.map(sectionToPayload),
      });
      message.success(t('workflow.customElements.savedStep'));
      setSaveStepTarget(null);
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.saveFailed'));
    } finally {
      setSavingStepAsCustom(false);
    }
  };

  const handleDelete = async (step: WorkflowStep) => {
    setBusyStepId(step.id);
    try {
      await workflowsApi.deleteStep(workflow.id, step.id);
      message.success(t('workflow.steps.deleted'));
      await refreshTemplate();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.deleteFailed'));
    } finally {
      setBusyStepId(null);
    }
  };

  const handleReorder = async (step: WorkflowStep, direction: ReorderDirection) => {
    setBusyStepId(step.id);
    try {
      await workflowsApi.reorderStep(workflow.id, step.id, direction);
      await refreshTemplate();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.reorderFailed'));
    } finally {
      setBusyStepId(null);
    }
  };

  const renderCard = (
    step: WorkflowStep,
    options: { number?: number; canMoveUp: boolean; canMoveDown: boolean; isFirstStep: boolean },
  ) => {
    // Locale-aware title + description with a graceful fallback to the other language
    // when one side is missing (legacy rows / originator left FR empty).
    const title = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
    const description =
      (isFr ? step.descriptionFr : step.descriptionEn) ||
      step.descriptionEn ||
      step.descriptionFr ||
      t('workflow.steps.noDescription');
    const reorderable = editable && step.type === 'DynamicForm';
    return (
      <div key={step.id} className="wf-step-card" style={CARD_STYLE}>
        {options.number !== undefined && (
          <>
            <span style={NUMBER_CIRCLE}>{options.number}</span>
            {/* Thin vertical rule between the number and the step info, matching the mock. */}
            <div style={{ alignSelf: 'stretch', width: 1, background: '#e5e7eb' }} />
          </>
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Wraps: a step can carry up to four chips beside its name, and on a
              non-wrapping row they hold their width while the NAME gives — which
              is what stacked it one character per line. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <Text strong style={{ fontSize: 14 }}>
              {title}
            </Text>
            {/* Inline "conditional" chip — only when step.conditions has rules.
                Restricted to DynamicForm steps since they're the only type
                whose visibility is authored (other types ship without the
                feature). Surfaces gating at a glance from the title side;
                the action button on the right opens the editor. */}
            {step.type === 'DynamicForm' && (step.conditions?.length ?? 0) > 0 && (
              <Tooltip
                title={t('workflow.steps.visibilityEdit', {
                  count: step.conditions!.length,
                })}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#0179C1',
                    background: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    padding: '1px 8px',
                    borderRadius: 999,
                  }}
                >
                  <ControlOutlined style={{ fontSize: 11 }} />
                  {t('workflow.steps.visibilityChip', {
                    count: step.conditions!.length,
                  })}
                </span>
              </Tooltip>
            )}
            {/* The "Disable if" twin of the chip above. Amber rather than blue so the
                two rules are distinguishable at a glance — a step can carry both, and
                they mean opposite things for whether its answers reach BMO. */}
            {step.type === 'DynamicForm' && (step.disableConditions?.length ?? 0) > 0 && (
              <Tooltip
                title={t('workflow.steps.disableEdit', {
                  count: step.disableConditions!.length,
                })}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#B45309',
                    background: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    padding: '1px 8px',
                    borderRadius: 999,
                  }}
                >
                  <StopOutlined style={{ fontSize: 11 }} />
                  {t('workflow.steps.disableChip', {
                    count: step.disableConditions!.length,
                  })}
                </span>
              </Tooltip>
            )}
            {/* "Advisor only" chip — the step is withheld from the public customer
                runtime. Sits beside the conditional chip because the two answer the
                same question from different angles ("when is this step shown?"), and
                a step can carry both. Edited from the pencil, like the title it
                qualifies. */}
            {step.advisorOnly === true && (
              <Tooltip title={t('workflow.steps.fields.advisorOnlyHelp')}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#92400E',
                    background: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    padding: '1px 8px',
                    borderRadius: 999,
                  }}
                >
                  <UserOutlined style={{ fontSize: 11 }} />
                  {t('workflow.steps.advisorOnlyChip')}
                </span>
              </Tooltip>
            )}
            {step.type === 'OneSpanESign' &&
              step.oneSpanConfig &&
              (() => {
                const { signers, placements } = summariseOneSpanConfig(step);
                if (signers === 0 && placements === 0) return null;
                // Pluralization key built up front so i18next picks the right variant
                // — both halves matter, so we synthesize a 4-way combo.
                const key =
                  signers > 1 && placements > 1
                    ? 'workflow.steps.oneSpan.summaryChip_both'
                    : signers > 1
                      ? 'workflow.steps.oneSpan.summaryChip_signers'
                      : placements > 1
                        ? 'workflow.steps.oneSpan.summaryChip_placements'
                        : 'workflow.steps.oneSpan.summaryChip';
                return (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 500,
                      color: '#0179C1',
                      background: '#EFF6FF',
                      border: '1px solid #BFDBFE',
                      padding: '1px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {t(key, { signers, placements })}
                  </span>
                );
              })()}
          </div>
          <div>
            {(() => {
              const meta = metaFor(step.type);
              return (
                <Tag
                  // Tinted chip matching the Add-Step picker so the type is recognisable at a glance.
                  style={{
                    marginInlineEnd: 0,
                    background: meta.tintBg,
                    color: meta.tintFg,
                    border: `1px solid ${meta.tintFg}33`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  icon={meta.icon()}
                >
                  {stepTypeLabel(step.type, t)}
                </Tag>
              );
            })()}
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {description}
          </Text>
        </div>
        <div
          className="wf-step-card__actions"
          style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}
        >
          {reorderable && (
            <>
              <Tooltip title={t('workflow.steps.moveUp')}>
                <Button
                  {...iconBtn}
                  aria-label={t('workflow.steps.moveUp')}
                  icon={<ArrowUpOutlined />}
                  disabled={!options.canMoveUp || busyStepId === step.id}
                  onClick={() => handleReorder(step, 'Up')}
                />
              </Tooltip>
              <Tooltip title={t('workflow.steps.moveDown')}>
                <Button
                  {...iconBtn}
                  aria-label={t('workflow.steps.moveDown')}
                  icon={<ArrowDownOutlined />}
                  disabled={!options.canMoveDown || busyStepId === step.id}
                  onClick={() => handleReorder(step, 'Down')}
                />
              </Tooltip>
            </>
          )}
          {/* Gear icon: PdfGeneration opens the PDF templates drawer — that authoring
              surface moved here on 2026-08-08 when PDF File Upload merged into this
              step. AdditionalDocuments opens the allowed-extensions picker. Other types
              stay stubbed until their per-type config lands. */}
          {step.type === 'PdfGeneration' && (
            <Tooltip title={t('workflow.steps.pdfEntries.openSettings')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.pdfEntries.openSettings')}
                icon={<SettingOutlined />}
                disabled={!editable}
                onClick={() => setPdfSettingsTarget(step)}
              />
            </Tooltip>
          )}
          {step.type === 'AdditionalDocuments' && (
            <Tooltip title={t('workflow.steps.additionalDocuments.openSettings')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.additionalDocuments.openSettings')}
                icon={<SettingOutlined />}
                disabled={!editable}
                onClick={() => setExtensionsTarget(step)}
              />
            </Tooltip>
          )}
          {step.type === 'DynamicForm' && (
            <Tooltip title={t('workflow.steps.openFormBuilder')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.openFormBuilder')}
                icon={<SettingOutlined />}
                disabled={!editable}
                onClick={() =>
                  navigate(`/originator/workflows/${workflow.id}/steps/${step.id}/edit`)
                }
              />
            </Tooltip>
          )}
          {step.type === 'OneSpanESign' && (
            <Tooltip title={t('workflow.steps.oneSpan.openSettings')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.oneSpan.openSettings')}
                icon={<SettingOutlined />}
                disabled={!editable}
                onClick={() => setOneSpanTarget(step)}
              />
            </Tooltip>
          )}
          {step.type === 'Welcome' && (
            <Tooltip title={t('workflow.steps.welcome.openSettings')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.welcome.openSettings')}
                icon={<SettingOutlined />}
                disabled={!editable}
                onClick={() => setWelcomeTarget(step)}
              />
            </Tooltip>
          )}
          {step.type === 'ThankYou' && (
            <Tooltip title={t('workflow.steps.thankYou.openSettings')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.thankYou.openSettings')}
                icon={<SettingOutlined />}
                disabled={!editable}
                onClick={() => setThankYouTarget(step)}
              />
            </Tooltip>
          )}
          {step.type !== 'PdfGeneration' &&
            step.type !== 'AdditionalDocuments' &&
            step.type !== 'DynamicForm' &&
            step.type !== 'OneSpanESign' &&
            step.type !== 'Welcome' &&
            step.type !== 'ThankYou' && (
              <Tooltip title={t('workflow.steps.configureComingSoon')}>
                <Button
                  {...iconBtn}
                  aria-label={t('workflow.steps.configureComingSoon')}
                  icon={<SettingOutlined />}
                  disabled
                />
              </Tooltip>
            )}
          {/* Only Dynamic Form steps are renameable. The other types ship with auto-defaults. */}
          {step.type === 'DynamicForm' && (
            <Tooltip title={t('workflow.steps.edit')}>
              <Button
                {...iconBtn}
                aria-label={t('workflow.steps.edit')}
                icon={<EditOutlined />}
                disabled={!editable}
                onClick={() => handleEdit(step)}
              />
            </Tooltip>
          )}
          {/* Visibility — step-level gating. Only DynamicForm steps support
              conditional visibility (other types ship with auto-defaults and
              are always shown). Source fields must live in PRIOR steps; hide
              the button entirely when there are no eligible sources AND no
              rules are already set (first step, or no dropdown/radio/etc.
              has been added to earlier steps). */}
          {step.type === 'DynamicForm' &&
            ((step.conditions?.length ?? 0) > 0 ||
              (!options.isFirstStep &&
                hasEligibleSourceFields(steps, { kind: 'step', ownStepId: step.id }))) && (
              <Tooltip
                title={
                  (step.conditions?.length ?? 0) > 0
                    ? t('workflow.steps.visibilityEdit', { count: step.conditions!.length })
                    : t('workflow.steps.visibilityAdd')
                }
              >
                <Button
                  {...iconBtn}
                  aria-label={
                    (step.conditions?.length ?? 0) > 0
                      ? t('workflow.steps.visibilityEdit', { count: step.conditions!.length })
                      : t('workflow.steps.visibilityAdd')
                  }
                  icon={<ControlOutlined />}
                  type={(step.conditions?.length ?? 0) > 0 ? 'primary' : 'default'}
                  disabled={!editable}
                  onClick={() => setVisibilityTarget(step)}
                />
              </Tooltip>
            )}
          {/* "Disable if" — same gating and the same source scope as the visibility
              button above, different effect: the step stays visible and navigable but
              every field on it renders read-only, and its answers still ship. */}
          {step.type === 'DynamicForm' &&
            ((step.disableConditions?.length ?? 0) > 0 ||
              (!options.isFirstStep &&
                hasEligibleSourceFields(steps, { kind: 'step', ownStepId: step.id }))) && (
              <Tooltip
                title={
                  (step.disableConditions?.length ?? 0) > 0
                    ? t('workflow.steps.disableEdit', { count: step.disableConditions!.length })
                    : t('workflow.steps.disableAdd')
                }
              >
                <Button
                  {...iconBtn}
                  aria-label={
                    (step.disableConditions?.length ?? 0) > 0
                      ? t('workflow.steps.disableEdit', { count: step.disableConditions!.length })
                      : t('workflow.steps.disableAdd')
                  }
                  icon={<StopOutlined />}
                  type={(step.disableConditions?.length ?? 0) > 0 ? 'primary' : 'default'}
                  danger={(step.disableConditions?.length ?? 0) > 0}
                  disabled={!editable}
                  onClick={() => setDisableTarget(step)}
                />
              </Tooltip>
            )}
          {/* Platform-managed steps (Application Review, PDF Generation) are mandatory —
              absent from the Add Step picker and refused by the BE on delete, so a
              delete button could only ever produce an error toast. Hidden rather than
              disabled: a greyed-out control invites a "why can't I?" that the tooltip
              can't answer usefully here. */}
          {!isPlatformManaged(step.type) && (
            <ConfirmPopover
              title={t('workflow.steps.deleteConfirmTitle')}
              description={t('workflow.steps.deleteConfirmDesc')}
              onConfirm={() => handleDelete(step)}
              disabled={!editable}
            >
              <Tooltip title={t('workflow.steps.delete')}>
                <Button
                  {...iconBtn}
                  aria-label={t('workflow.steps.delete')}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!editable}
                />
              </Tooltip>
            </ConfirmPopover>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Wraps at every width: Preview + Add Step are ~230px of intrinsic
          width, so wherever the pane is narrower than heading + buttons the
          buttons take their own line instead of crushing the heading.

          In global.css rather than a `style` object because the phone band
          shrinks these two controls out of the form's 56px primary-action
          height and lets them stack — an inline declaration here would outrank
          that band. */}
      <div className="wf-steps-header">
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
          {t('workflow.details.stepsTitle')}
        </h2>
        <div className="wf-steps-header__actions">
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/originator/workflows/${workflow.id}/preview`)}
          >
            {t('workflow.builder.preview')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!editable}
            onClick={() => setAddOpen(true)}
          >
            {t('workflow.details.addStep')}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
        {steps.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: '#6b7280',
              fontSize: 14,
              border: '1px dashed #e5e7eb',
              borderRadius: 10,
            }}
          >
            {t('workflow.details.stepsEmpty')}
          </div>
        ) : (
          <>
            {steps.map((step, idx) => {
              // Reorder only swaps Dynamic Forms with adjacent Dynamic Forms;
              // every other type is pinned by CanonicalRank, so no arrows.
              const dynamicIdx =
                step.type === 'DynamicForm' ? dynamicForms.findIndex((d) => d.id === step.id) : -1;
              return renderCard(step, {
                number: idx + 1,
                canMoveUp: dynamicIdx > 0,
                canMoveDown: dynamicIdx >= 0 && dynamicIdx < dynamicForms.length - 1,
                // The first step has no prior step, so step-level conditions are
                // meaningless there.
                isFirstStep: idx === 0,
              });
            })}
          </>
        )}
      </div>

      <AddStepModal
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        existingSteps={steps}
        onPick={handleAdd}
        onPickCustom={handleAddCustom}
      />
      <EditStepModal
        step={editTarget}
        onCancel={() => setEditTarget(null)}
        onSubmit={handleEditSubmit}
        onSaveAsCustom={
          editTarget?.type === 'DynamicForm'
            ? () => {
                setSaveStepTarget(editTarget);
                setEditTarget(null);
              }
            : undefined
        }
        onAdvanced={
          editTarget?.type === 'DynamicForm' ? () => setAdvancedTarget(editTarget) : undefined
        }
      />
      <StepAdvancedModal
        templateId={workflow.id}
        // Re-resolved from the live template, not from the captured target: after
        // refreshTemplate the fresh rules live on workflow.steps, and the drawers below
        // re-resolve for the same reason.
        step={
          advancedTarget ? (workflow.steps.find((s) => s.id === advancedTarget.id) ?? null) : null
        }
        editable={editable}
        onClose={() => setAdvancedTarget(null)}
        onChanged={refreshTemplate}
      />
      <SaveAsCustomElementModal
        open={saveStepTarget !== null}
        kind="Step"
        saving={savingStepAsCustom}
        onCancel={() => setSaveStepTarget(null)}
        onSubmit={handleSaveStepAsCustom}
      />
      {/* Step-level visibility editor — source fields restricted to PRIOR steps
          by the modal's scope filter. Save persists then refreshes the
          workflow so the eye-icon badge reflects new condition counts. */}
      <ConditionsEditorModal
        open={visibilityTarget !== null}
        title={t('workflow.steps.visibilityModalTitle')}
        intro={t('workflow.steps.visibilityIntro')}
        initialConditions={visibilityTarget?.conditions ?? []}
        initialCombinator={visibilityTarget?.conditionCombinator ?? null}
        allSteps={steps}
        scope={
          visibilityTarget ? { kind: 'step', ownStepId: visibilityTarget.id } : { kind: 'pdf' }
        }
        saving={savingVisibility}
        onCancel={() => setVisibilityTarget(null)}
        onSave={async (conditions, combinator) => {
          if (!visibilityTarget) return;
          setSavingVisibility(true);
          try {
            await workflowsApi.setStepConditions(workflow.id, visibilityTarget.id, {
              conditions,
              combinator,
            });
            setVisibilityTarget(null);
            await refreshTemplate();
          } catch (err) {
            handleApiError(err, t, t('workflow.steps.visibilitySaveFailed'));
          } finally {
            setSavingVisibility(false);
          }
        }}
      />
      {/* Step-level "Disable if" editor. Same scope and the same shared modal as the
          visibility editor above — only the copy and the endpoint differ. */}
      <ConditionsEditorModal
        open={disableTarget !== null}
        title={t('workflow.steps.disableModalTitle')}
        intro={t('workflow.steps.disableIntro')}
        initialConditions={disableTarget?.disableConditions ?? []}
        initialCombinator={disableTarget?.disableConditionCombinator ?? null}
        allSteps={steps}
        scope={disableTarget ? { kind: 'step', ownStepId: disableTarget.id } : { kind: 'pdf' }}
        saving={savingDisable}
        onCancel={() => setDisableTarget(null)}
        onSave={async (conditions, combinator) => {
          if (!disableTarget) return;
          setSavingDisable(true);
          try {
            await workflowsApi.setStepDisableConditions(workflow.id, disableTarget.id, {
              conditions,
              combinator,
            });
            setDisableTarget(null);
            await refreshTemplate();
          } catch (err) {
            handleApiError(err, t, t('workflow.steps.disableSaveFailed'));
          } finally {
            setSavingDisable(false);
          }
        }}
      />
      {pdfSettingsTarget && (
        <PdfEntriesDrawer
          templateId={workflow.id}
          step={
            // After refreshTemplate, the fresh step content lives on workflow.steps —
            // re-resolve so the drawer renders the latest fileEntries list.
            workflow.steps.find((s) => s.id === pdfSettingsTarget.id) ?? null
          }
          // All DynamicForm steps in the workflow — the per-PDF condition modal
          // needs them to enumerate selectable source fields.
          allSteps={workflow.steps}
          editable={editable}
          onClose={() => setPdfSettingsTarget(null)}
          onChanged={refreshTemplate}
        />
      )}
      {extensionsTarget && (
        <AdditionalDocumentEntriesDrawer
          templateId={workflow.id}
          step={workflow.steps.find((s) => s.id === extensionsTarget.id) ?? null}
          allSteps={workflow.steps}
          editable={editable}
          onClose={() => setExtensionsTarget(null)}
          onChanged={refreshTemplate}
        />
      )}
      {oneSpanTarget && (
        <OneSpanConfigDrawer
          templateId={workflow.id}
          workflow={workflow}
          step={workflow.steps.find((s) => s.id === oneSpanTarget.id) ?? null}
          editable={editable}
          onClose={() => setOneSpanTarget(null)}
          onChanged={refreshTemplate}
        />
      )}
      {welcomeTarget && (
        <WelcomeConfigModal
          templateId={workflow.id}
          workflow={workflow}
          step={workflow.steps.find((s) => s.id === welcomeTarget.id) ?? null}
          editable={editable}
          onClose={() => setWelcomeTarget(null)}
          onChanged={refreshTemplate}
        />
      )}
      {thankYouTarget && (
        <ThankYouConfigModal
          templateId={workflow.id}
          workflow={workflow}
          step={workflow.steps.find((s) => s.id === thankYouTarget.id) ?? null}
          editable={editable}
          onClose={() => setThankYouTarget(null)}
          onChanged={refreshTemplate}
        />
      )}
    </>
  );
}
